// Update to src/components/QuizHub.jsx
import React from "react";
import { motion } from "framer-motion";

const QuizHub = ({ theme, setCurrentView }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  // Quiz options
  const quizzes = [
    {
      id: "shopaholic-test",
      title: "Shopping Addiction Assessment",
      description: "Evaluate your shopping habits and determine if they show signs of compulsive buying disorder.",
      emoji: "🛍️",
      gradient: "from-red-100 to-pink-100 dark:from-red-900/30 dark:to-pink-900/30",
      ctaText: "Take Assessment",
      comingSoon: false,
      questions: 13,
      duration: "5-7 min",
      shareEnabled: true
    },
    {
      id: "shopping-personality-quiz",
      title: "Shopping Personality Quiz",
      description: "Discover your unique shopping personality type and get personalized advice.",
      emoji: "💸",
      gradient: "from-blue-100 to-teal-100 dark:from-blue-900/30 dark:to-teal-900/30",
      ctaText: "Take Quiz",
      comingSoon: false,
      questions: 10,
      duration: "4-5 min",
      shareEnabled: true
    },
    {
      id: "financial-values-quiz",
      title: "Financial Values Assessment",
      description: "Uncover your core financial values and how they influence your spending decisions.",
      emoji: "💰",
      gradient: "from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30",
      ctaText: "Take Assessment",
      comingSoon: false,
      questions: 10,
      duration: "5-6 min",
      shareEnabled: true
    },
    {
      id: "emotional-spending-quiz",
      title: "Emotional Spending Trigger Quiz",
      description: "Identify your unique emotional triggers for shopping and learn personalized strategies to shop more mindfully.",
      emoji: "🧠",
      gradient: "from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30",
      ctaText: "Take Quiz",
      comingSoon: false,
      questions: 12,
      duration: "5-6 min",
      shareEnabled: true
    },
    {
      id: "guilt-triggers",
      title: "Shopping Guilt Triggers Test",
      description: "Identify what specifically triggers your shopping guilt and learn how to address it.",
      emoji: "😬",
      gradient: "from-amber-100 to-yellow-100 dark:from-amber-900/30 dark:to-yellow-900/30",
      ctaText: "Coming Soon",
      comingSoon: true,
      questions: 8,
      duration: "3-4 min",
      shareEnabled: true
    },
    {
      id: "financial-literacy",
      title: "Financial Literacy Quiz",
      description: "Test your knowledge about personal finance, budgeting, and saving strategies.",
      emoji: "📚",
      gradient: "from-purple-100 to-indigo-100 dark:from-purple-900/30 dark:to-indigo-900/30",
      ctaText: "Coming Soon",
      comingSoon: true,
      questions: 12,
      duration: "5-6 min",
      shareEnabled: true
    }
  ];

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold mb-3 dark:text-white">Guilt Gauge Quizzes</h1>
        <p className="text-gray-600 dark:text-gray-300">
          Take our free quizzes to gain insights about your shopping habits and emotional spending patterns.
        </p>
      </div>
      
      <div className="grid grid-cols-1 gap-4">
        {quizzes.map(quiz => (
          <motion.div 
            key={quiz.id}
            className={`bg-gradient-to-r ${quiz.gradient} rounded-xl p-5`}
            whileHover={{ y: -5 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex justify-between items-start mb-3">
              <div>
                <h2 className="text-xl font-bold dark:text-white">{quiz.title}</h2>
                <p className="text-gray-700 dark:text-gray-300 text-sm mt-1">
                  {quiz.description}
                </p>
                <div className="flex items-center mt-2 text-xs text-gray-500 dark:text-gray-400">
                  <span className="mr-3">{quiz.questions} questions</span>
                  <span>{quiz.duration}</span>
                  {quiz.shareEnabled && (
                    <span className="ml-3 bg-white dark:bg-gray-700 px-2 py-1 rounded-full flex items-center">
                      <span className="mr-1">🔗</span> Shareable Results
                    </span>
                  )}
                </div>
              </div>
              <div className="text-4xl">{quiz.emoji}</div>
            </div>
            
            {quiz.comingSoon ? (
              <div className="bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-xl py-2.5 text-center text-sm font-medium cursor-not-allowed">
                Coming Soon
              </div>
            ) : (
              <motion.button
                onClick={() => setCurrentView(quiz.id)}
                className={`w-full py-2.5 rounded-xl text-white font-medium text-sm ${buttonStyles[theme]}`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {quiz.ctaText}
              </motion.button>
            )}
          </motion.div>
        ))}
      </div>
      
      <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl">
        <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2 flex items-center">
          <span className="mr-2">ℹ️</span> Why Take These Quizzes?
        </h3>
        <p className="text-sm text-blue-700 dark:text-blue-200">
          Understanding your shopping habits, financial values, and emotional triggers can help you make more mindful spending decisions. Our quizzes provide personalized insights and recommendations to improve your financial wellbeing.
        </p>
      </div>
      
      <div className="mt-4 text-center">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          These quizzes are for informational purposes only and are not diagnostic tools.
          If you're concerned about your shopping habits, please consult with a mental health professional.
        </p>
      </div>
    </motion.div>
  );
};

export default QuizHub;
